import React, { useState, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, Paper, Grid, Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    minWidth: 600,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      padding: theme.spacing(1.5),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  monthTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#1e293b',
    textTransform: 'capitalize',
  },
  calendarsContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  calendar: {
    flex: 1,
    minWidth: 260,
  },
  weekDays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: theme.spacing(1),
    borderBottom: '1px solid #e2e8f0',
    paddingBottom: theme.spacing(1),
  },
  weekDay: {
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#64748b',
    textTransform: 'uppercase',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
  },
  dayCell: {
    aspectRatio: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    cursor: 'pointer',
    borderRadius: 8,
    transition: 'all 0.15s ease',
    position: 'relative',
    minHeight: 36,
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  dayEmpty: {
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  dayDisabled: {
    color: '#cbd5e1',
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  daySelected: {
    backgroundColor: '#059669',
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#047857',
    },
  },
  dayInRange: {
    backgroundColor: '#d1fae5',
    borderRadius: 0,
    '&:hover': {
      backgroundColor: '#a7f3d0',
    },
  },
  dayRangeStart: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
  },
  dayRangeEnd: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  dayToday: {
    border: '2px solid #059669',
    fontWeight: 600,
  },
  quickActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: '1px solid #e2e8f0',
  },
  quickChip: {
    backgroundColor: '#f1f5f9',
    '&:hover': {
      backgroundColor: '#e2e8f0',
    },
  },
  quickChipActive: {
    backgroundColor: '#059669',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#047857',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #e2e8f0',
  },
  selectedRange: {
    fontSize: '0.875rem',
    color: '#64748b',
  },
  selectedRangeHighlight: {
    fontWeight: 600,
    color: '#1e293b',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  applyButton: {
    backgroundColor: '#059669',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#047857',
    },
    '&:disabled': {
      backgroundColor: '#94a3b8',
    },
  },
  cancelButton: {
    textTransform: 'none',
    color: '#64748b',
  },
}));

const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const QUICK_RANGES = [
  { label: '7 derniers jours', days: 7 },
  { label: '14 derniers jours', days: 14 },
  { label: '30 derniers jours', days: 30 },
  { label: '90 derniers jours', days: 90 },
];

const formatDateFr = (date) => {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatDateISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

const isInRange = (date, start, end) => {
  if (!start || !end || !date) return false;
  const d = date.getTime();
  const s = start.getTime();
  const e = end.getTime();
  return d > Math.min(s, e) && d < Math.max(s, e);
};

const DateRangePicker = ({
  startDate,
  endDate,
  onApply,
  onCancel,
  maxDate = new Date()
}) => {
  const classes = useStyles();

  // Convertir les dates string en Date
  const initialStart = startDate ? new Date(startDate) : null;
  const initialEnd = endDate ? new Date(endDate) : null;

  const [tempStart, setTempStart] = useState(initialStart);
  const [tempEnd, setTempEnd] = useState(initialEnd);
  const [hoverDate, setHoverDate] = useState(null);
  const [activeQuickRange, setActiveQuickRange] = useState(null);

  // Mois affiché (pour le calendrier de gauche)
  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    // Afficher le mois précédent à gauche, le mois courant à droite
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Générer les jours d'un mois
  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Jour de la semaine du premier jour (0 = dimanche, 1 = lundi, etc.)
    let startDayOfWeek = firstDay.getDay();
    // Convertir pour commencer par lundi (0 = lundi, 6 = dimanche)
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    const days = [];

    // Jours vides au début
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    // Jours du mois
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const leftMonth = viewMonth;
  const rightMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);

  const leftDays = useMemo(() =>
    getDaysInMonth(leftMonth.getFullYear(), leftMonth.getMonth()),
    [leftMonth]
  );

  const rightDays = useMemo(() =>
    getDaysInMonth(rightMonth.getFullYear(), rightMonth.getMonth()),
    [rightMonth]
  );

  const handlePrevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);
    // Ne pas dépasser le mois courant pour le calendrier de droite
    if (nextMonth.getFullYear() < today.getFullYear() ||
        (nextMonth.getFullYear() === today.getFullYear() && nextMonth.getMonth() <= today.getMonth())) {
      setViewMonth(nextMonth);
    }
  };

  const handleDayClick = (date) => {
    if (!date || date > maxDate) return;

    setActiveQuickRange(null);

    if (!tempStart || (tempStart && tempEnd)) {
      // Première sélection ou reset
      setTempStart(date);
      setTempEnd(null);
    } else {
      // Deuxième sélection
      if (date < tempStart) {
        setTempEnd(tempStart);
        setTempStart(date);
      } else {
        setTempEnd(date);
      }
    }
  };

  const handleQuickRange = (days) => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date();
    start.setDate(start.getDate() - days + 1);
    start.setHours(0, 0, 0, 0);

    setTempStart(start);
    setTempEnd(end);
    setActiveQuickRange(days);
  };

  const handleApply = () => {
    if (tempStart && tempEnd) {
      onApply({
        startDate: formatDateISO(tempStart),
        endDate: formatDateISO(tempEnd),
      });
    }
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const getDayClasses = (date) => {
    if (!date) return `${classes.dayCell} ${classes.dayEmpty}`;

    const classNames = [classes.dayCell];

    // Désactivé si dans le futur
    if (date > maxDate) {
      classNames.push(classes.dayDisabled);
      return classNames.join(' ');
    }

    // Aujourd'hui
    if (isSameDay(date, today)) {
      classNames.push(classes.dayToday);
    }

    const effectiveEnd = tempEnd || hoverDate;
    const isStart = isSameDay(date, tempStart);
    const isEnd = isSameDay(date, effectiveEnd);

    // Sélectionné (début ou fin)
    if (isStart || isEnd) {
      classNames.push(classes.daySelected);
      if (tempStart && effectiveEnd) {
        if (isStart && !isSameDay(tempStart, effectiveEnd)) {
          classNames.push(classes.dayRangeStart);
        }
        if (isEnd && !isSameDay(tempStart, effectiveEnd)) {
          classNames.push(classes.dayRangeEnd);
        }
      }
    } else if (tempStart && effectiveEnd && isInRange(date, tempStart, effectiveEnd)) {
      // Dans la plage
      classNames.push(classes.dayInRange);
    }

    return classNames.join(' ');
  };

  const renderCalendar = (days, monthDate) => (
    <div className={classes.calendar}>
      <Typography className={classes.monthTitle} align="center" gutterBottom>
        {getMonthName(monthDate)}
      </Typography>
      <div className={classes.weekDays}>
        {WEEK_DAYS.map((day) => (
          <div key={day} className={classes.weekDay}>{day}</div>
        ))}
      </div>
      <div className={classes.daysGrid}>
        {days.map((date, index) => (
          <div
            key={index}
            className={getDayClasses(date)}
            onClick={() => handleDayClick(date)}
            onMouseEnter={() => {
              if (tempStart && !tempEnd && date && date <= maxDate) {
                setHoverDate(date);
              }
            }}
            onMouseLeave={() => setHoverDate(null)}
          >
            {date ? date.getDate() : ''}
          </div>
        ))}
      </div>
    </div>
  );

  const canGoNext = () => {
    const nextMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 2, 1);
    return nextMonth <= new Date(today.getFullYear(), today.getMonth() + 1, 1);
  };

  return (
    <Paper className={classes.container} elevation={0}>
      {/* Raccourcis rapides */}
      <div className={classes.quickActions}>
        {QUICK_RANGES.map((range) => (
          <Chip
            key={range.days}
            label={range.label}
            size="small"
            onClick={() => handleQuickRange(range.days)}
            className={activeQuickRange === range.days ? classes.quickChipActive : classes.quickChip}
          />
        ))}
      </div>

      {/* Navigation et calendriers */}
      <div className={classes.header}>
        <IconButton size="small" onClick={handlePrevMonth}>
          <ChevronLeftIcon />
        </IconButton>
        <div style={{ flex: 1 }} />
        <IconButton size="small" onClick={handleNextMonth} disabled={!canGoNext()}>
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className={classes.calendarsContainer}>
        {renderCalendar(leftDays, leftMonth)}
        {renderCalendar(rightDays, rightMonth)}
      </div>

      {/* Footer avec sélection et actions */}
      <div className={classes.footer}>
        <div className={classes.selectedRange}>
          {tempStart && tempEnd ? (
            <>
              <span className={classes.selectedRangeHighlight}>{formatDateFr(tempStart)}</span>
              {' - '}
              <span className={classes.selectedRangeHighlight}>{formatDateFr(tempEnd)}</span>
            </>
          ) : tempStart ? (
            <>
              <span className={classes.selectedRangeHighlight}>{formatDateFr(tempStart)}</span>
              {' - Sélectionnez la date de fin'}
            </>
          ) : (
            'Sélectionnez une plage de dates'
          )}
        </div>
        <div className={classes.actions}>
          <Button
            className={classes.cancelButton}
            onClick={onCancel}
            size="small"
          >
            Annuler
          </Button>
          <Button
            className={classes.applyButton}
            onClick={handleApply}
            disabled={!tempStart || !tempEnd}
            size="small"
            variant="contained"
          >
            Appliquer
          </Button>
        </div>
      </div>
    </Paper>
  );
};

export default DateRangePicker;
